import { DefaultAssociation } from './messageTypes';
import { isSome, keysOf } from '../../utils/common';
import { handleError } from '../../utils/errorHandling';
export class AssociationBuilder {
  constructor() {
    this.associations = {
      [DefaultAssociation.SUBSCRIPTION_TO_TICKET]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_COMMERCE_PAYMENT]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_INVOICE]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_DEAL]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_QUOTE]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_CONTRACT]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_CONTACT]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_COMPANY]: [],
      [DefaultAssociation.SUBSCRIPTION_TO_PAYMENT_LINK]: []
    };
  }
  of(existingAssociations) {
    this.associations = mergeAssociations(existingAssociations, this.associations);
    return this;
  }
  isEmpty() {
    return Object.values(this.associations).every(value => value.length === 0);
  }
  withContactId(contactId) {
    this.associations.SUBSCRIPTION_TO_CONTACT = isSome(contactId) ? [contactId] : [];
    return this;
  }
  withCompanyId(companyId) {
    this.associations.SUBSCRIPTION_TO_COMPANY = isSome(companyId) ? [companyId] : [];
    return this;
  }
  withQuoteId(quoteId) {
    this.associations.SUBSCRIPTION_TO_QUOTE = isSome(quoteId) ? [quoteId] : [];
    return this;
  }
  withContractId(contractId) {
    this.associations.SUBSCRIPTION_TO_CONTRACT = isSome(contractId) ? [contractId] : [];
    return this;
  }
  withPaymentLinkId(paymentLinkId) {
    this.associations.SUBSCRIPTION_TO_PAYMENT_LINK = isSome(paymentLinkId) ? [paymentLinkId] : [];
    return this;
  }
  withTicketId(...ticketIds) {
    this.associations.SUBSCRIPTION_TO_TICKET = Array.from(new Set([...this.associations.SUBSCRIPTION_TO_TICKET, ...ticketIds]));
    return this;
  }
  withInvoiceId(...invoiceIds) {
    this.associations.SUBSCRIPTION_TO_INVOICE = Array.from(new Set([...this.associations.SUBSCRIPTION_TO_INVOICE, ...invoiceIds]));
    return this;
  }
  withDealId(...dealIds) {
    this.associations.SUBSCRIPTION_TO_DEAL = Array.from(new Set([...this.associations.SUBSCRIPTION_TO_DEAL, ...dealIds]));
    return this;
  }
  withCommercePaymentId(...commercePaymentIds) {
    this.associations.SUBSCRIPTION_TO_COMMERCE_PAYMENT = Array.from(new Set([...this.associations.SUBSCRIPTION_TO_COMMERCE_PAYMENT, ...commercePaymentIds]));
    return this;
  }
  withAssociation(key, value) {
    this.associations[key] = value;
    return this;
  }

  /** merge partial associations with existing associations
   * @note: this is to handle the case where we have a PartialAssociation map
   * and we want to merge it with the existing associations without clobbering existing values */
  merge(partialAssociations) {
    this.associations = mergeAssociations(partialAssociations, this.associations);
    return this;
  }
  build() {
    return this.associations;
  }
}

/** merge partial associations with existing associations
 * @note: this is to handle the case where we have a PartialAssociation map
 * and we want to merge it with the existing associations without clobbering existing values */
export function mergeAssociations(partialAssociations, existingAssociations = new AssociationBuilder().build()) {
  const result = new AssociationBuilder().build();
  if (!partialAssociations) {
    return existingAssociations;
  }
  for (const key of keysOf(result)) {
    switch (key) {
      // these are all 1:1 on the subscription side, so we need to ensure there is only one value
      case DefaultAssociation.SUBSCRIPTION_TO_CONTRACT:
      case DefaultAssociation.SUBSCRIPTION_TO_PAYMENT_LINK:
      case DefaultAssociation.SUBSCRIPTION_TO_QUOTE:
      case DefaultAssociation.SUBSCRIPTION_TO_CONTACT:
      case DefaultAssociation.SUBSCRIPTION_TO_COMPANY:
        {
          var _partialAssociations$, _partialAssociations$2, _partialAssociations$3, _existingAssociations;
          if (((_partialAssociations$ = partialAssociations === null || partialAssociations === void 0 || (_partialAssociations$2 = partialAssociations[key]) === null || _partialAssociations$2 === void 0 ? void 0 : _partialAssociations$2.length) !== null && _partialAssociations$ !== void 0 ? _partialAssociations$ : 0) > 1) {
            handleError(new Error(`Association ${key} has more than one value, this is not supported (1:1 association)
                partialAssociations: ${JSON.stringify(partialAssociations)}
                `));
          }
          const [updateValue] = (_partialAssociations$3 = partialAssociations === null || partialAssociations === void 0 ? void 0 : partialAssociations[key]) !== null && _partialAssociations$3 !== void 0 ? _partialAssociations$3 : [];
          const [existingValue] = (_existingAssociations = existingAssociations[key]) !== null && _existingAssociations !== void 0 ? _existingAssociations : [];
          const value = updateValue !== null && updateValue !== void 0 ? updateValue : existingValue;
          result[key] = [...(value ? [value] : [])];
          break;
        }
      // Everything else is 1:many on the subscription side, so we can just use the value directly
      default:
        {
          var _partialAssociations$4, _existingAssociations2;
          const update = (_partialAssociations$4 = partialAssociations === null || partialAssociations === void 0 ? void 0 : partialAssociations[key]) !== null && _partialAssociations$4 !== void 0 ? _partialAssociations$4 : [];
          const existing = (_existingAssociations2 = existingAssociations[key]) !== null && _existingAssociations2 !== void 0 ? _existingAssociations2 : [];
          if (update.length && existing.length) {
            result[key] = Array.from(new Set([...existing, ...update]));
          } else if (update.length) {
            result[key] = update;
          } else {
            result[key] = existing;
          }
          break;
        }
    }
  }
  return result;
}